import {
    FormControl, FormErrorMessage, FormHelperText,
    FormLabel, InputGroup, InputRightElement,
    InputLeftElement, chakra, type SystemProps
} from '@chakra-ui/react';
import type {
    FormControlProps
} from '@chakra-ui/react';
import React, {
    forwardRef
} from 'react';

import { BaseControlState, type ControlBaseType, useBaseControlTheme } from './use-base-control-theme';

export type BaseControlComponentProps<ControlProps extends ControlBaseType> =
    & Omit<FormControlProps, 'defaultValue' | 'value' | 'onChange'>
    & {
    /**
     * The unique id given to the control for usage in the form.
     */
    id: string
    /**
     * Helper attribute convention for testing libraries.
     */
    'data-testid'?: string
    /**
     * The text for the label on top of the input, used to describe the purpose of the input.
     */
    labelText?: string | null
    /**
     * Text for showing the user more descriptive content than the label,
     * eg error message text and ux suggestions on about the data to input.
     */
    helperText?: string | null
    /**
     * A warning state to show the user something requires attention.
     */
    isWarning?: boolean
    /**
     * A state to show the user that something has worked correctly.
     */
    isSuccess?: boolean
    /**
     * A custom warning state to show the user something went wrong with the user account.
     */
    isUserWarning?: boolean
    /**
     * A React element to render to the right and inside the input, eg an icon or visual aid.
     */
    RightAdornment?: React.ReactElement | null
    /**
     * A React element to render to the right and inside the input, eg an icon or visual aid.
     */
    LeftAdornment?: React.ReactElement | null
    /**
     * The actual control component to render, eg an Input / Select or third party Component.
     */
    renderControl: (props: ControlProps) => JSX.Element
    /**
     * Prop middleware to mutate the props of the Chakra top level FormControl before the controlProps are passed down.
     */
    assignProps?: (
        props: Omit<BaseControlComponentProps<ControlProps>, 'assignProps'>,
        state: BaseControlState
    ) => any
    assignControlProps?: (
        controlProps: ControlProps,
        props: Omit<BaseControlComponentProps<ControlProps>, 'assignProps'>,
        state: BaseControlState
    ) => any
    /**
     * The control element to render inside this base control, could be something like Input / Select or from a Third Party.
     */
    controlProps?: ControlProps
    /**
     * Styles for the inner wrapper div element of the control.
     */
    wrapperStyles?: SystemProps
    /**
     * Override the default behaviour for determining if the input has any content,
     * this is used for the material ui UX with the label animation.
     */
    hasValuePredicate?: (
        value: unknown,
        props: Omit<BaseControlComponentProps<ControlProps>, 'hasValuePredicate'>
    ) => boolean
    /**
     * Prop used to enable hover state by default for testing styles.
     */
    testHover?: boolean
    /**
     * Prop used to enable focused state by default for testing styles.
     */
    testFocused?: boolean
    /**
     * forwarded React.Ref used for the base so that Generics can work
     */
    mRef?: React.Ref<any>
};

export type BaseControlProps<ControlProps extends ControlBaseType> = Omit<
    BaseControlComponentProps<ControlProps>, 'renderControl' | 'defaultValue'
    >;

function BaseControlInner<ControlProps extends ControlBaseType> (
    originalProps: BaseControlComponentProps<ControlProps>,
    ref: React.ForwardedRef<any>
) {
    const {
        formControlProps,
        id,
        hasValue,
        styles,
        state: { isInvalid },
        labelText,
        onOver,
        onOut,
        wrapperStyles,
        controlRenderProps,
        renderControl,
        helperText,
        LeftAdornment,
        RightAdornment
    } = useBaseControlTheme<ControlProps>(
        originalProps,
        ref
    );

    return (
        <FormControl
            {...formControlProps}
            data-id={id}
            data-has-value={hasValue}
            position={'relative'}
            sx={styles.FormControl}
            onMouseOver={onOver}
            onMouseOut={onOut}
        >
            {labelText ?
                <FormLabel
                    htmlFor={id}
                    noOfLines={1}
                    sx={styles.FormLabel}
                    title={labelText}
                >
                    {labelText}
                </FormLabel> : null}

            <InputGroup>
                {LeftAdornment ?
                    <InputLeftElement
                        sx={styles.InputLeftElement}
                        children={LeftAdornment}
                    /> : null}

                <chakra.div
                    data-testid={'wrapper'}
                    sx={wrapperStyles}
                >
                    {renderControl(controlRenderProps)}
                </chakra.div>

                {RightAdornment ?
                    <InputRightElement
                        sx={styles.InputRightElement}
                        children={RightAdornment}
                    /> : null}
            </InputGroup>

            {helperText ?
                isInvalid ?
                    <FormErrorMessage
                        data-testid={`helper-text-${id}`}
                        data-label-type={'error'}
                        sx={styles.FormErrorMessage}
                    >
                        {helperText}
                    </FormErrorMessage> :
                    <FormHelperText
                        data-testid={`helper-text-${id}`}
                        data-label-type={'helper'}
                        sx={styles.FormHelperText}
                    >
                        {helperText}
                    </FormHelperText> : null
            }
        </FormControl>
    );
}

// This pattern is used to make generics work, there maybe a better way?
// ref: https://fettblog.eu/typescript-react-generic-forward-refs
const BaseControlWithRef = forwardRef(BaseControlInner);

export function BaseControl<T extends ControlBaseType> ({
    mRef,
    ...props
}: BaseControlComponentProps<T>) {
    return <BaseControlWithRef ref={mRef} {...props as any} />;
}
